/**
 * Package Capability Matrix
 *
 * Defines which fiduciary roles, trust rules, and clauses apply to each
 * package type. This is the single source of truth for role availability
 * and successor depth limits across all packages.
 *
 * All three packages share identical fiduciary role structures.
 * Differences are limited to survivorship (married only),
 * spouse-related fields, and single-person package restrictions.
 */

// ---------------------------------------------------------------------------
// Role definitions with max successor depths
// ---------------------------------------------------------------------------

const FIDUCIARY_ROLES = {
  executor: {
    label: 'Executor',
    maxSuccessors: 2,
    allowsPair: true,
    hasInitialAppointment: true, // primary executor is named
  },
  trustee: {
    label: 'Trustee',
    maxSuccessors: 3,
    allowsPair: true,
    hasInitialAppointment: false, // client is always initial trustee (implicit)
  },
  trust_protector: {
    label: 'Trust Protector',
    maxSuccessors: 3,
    allowsPair: true,
    hasInitialAppointment: false, // trust protector appointment structure varies
  },
  estate_protector: {
    label: 'Estate Protector',
    maxSuccessors: 0,
    allowsPair: false,
    hasInitialAppointment: true,
  },
  guardian: {
    label: 'Guardian',
    maxSuccessors: 1,
    allowsPair: true,
    hasInitialAppointment: true,
    conditionalOn: 'has_minor_children',
  },
  financial_agent: {
    label: 'Financial Agent',
    maxSuccessors: 1,
    allowsPair: true,
    hasInitialAppointment: true,
  },
  healthcare_agent: {
    label: 'Healthcare Agent',
    maxSuccessors: 2,
    allowsPair: false,
    hasInitialAppointment: true,
    requiresContact: true,
  },
};

// ---------------------------------------------------------------------------
// Package-specific overrides and rules
// ---------------------------------------------------------------------------

const PACKAGE_RULES = {
  'Disclaimer Package': {
    marriedPackage: true,
    hasSurvivorshipClause: true,
    hasDescendantsSingleTrust: 'conditional', // only if has_minor_children
    spouseAsInitialSuccessorTrustee: 'attorney_choice',
    roles: Object.keys(FIDUCIARY_ROLES), // all roles available
  },
  'Fractional QTIP Package': {
    marriedPackage: true,
    hasSurvivorshipClause: true,
    hasDescendantsSingleTrust: 'conditional',
    spouseAsInitialSuccessorTrustee: 'attorney_choice',
    roles: Object.keys(FIDUCIARY_ROLES),
  },
  'Probate Avoidance Package': {
    marriedPackage: false,
    hasSurvivorshipClause: false,
    hasDescendantsSingleTrust: 'conditional',
    spouseAsInitialSuccessorTrustee: 'not_applicable',
    roles: Object.keys(FIDUCIARY_ROLES),
  },
  'Healthcare Documents Package': {
    marriedPackage: false,
    hasSurvivorshipClause: false,
    hasDescendantsSingleTrust: false,
    spouseAsInitialSuccessorTrustee: 'not_applicable',
    roles: ['healthcare_agent'],
  },
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Get the full role definition for a fiduciary role.
 * @param {string} roleId - Role identifier (e.g., 'trustee', 'executor')
 * @returns {object|null} Role definition or null if unknown
 */
export function getRoleDefinition(roleId) {
  return FIDUCIARY_ROLES[roleId] || null;
}

/**
 * Get all role IDs available for a package type.
 * @param {string} packageType - Package name
 * @returns {string[]} Array of role IDs
 */
export function getRolesForPackage(packageType) {
  const rules = PACKAGE_RULES[packageType];
  if (!rules) return [];
  return rules.roles;
}

/**
 * Check if a role is allowed in a given package.
 * @param {string} roleId - Role identifier
 * @param {string} packageType - Package name
 * @returns {boolean}
 */
export function isRoleAllowed(roleId, packageType) {
  const rules = PACKAGE_RULES[packageType];
  if (!rules) return false;
  return rules.roles.includes(roleId);
}

/**
 * Get the maximum successor depth for a role.
 * @param {string} roleId - Role identifier
 * @returns {number} Max successor tiers (0 = no successors allowed)
 */
export function getMaxSuccessorDepth(roleId) {
  const role = FIDUCIARY_ROLES[roleId];
  return role ? role.maxSuccessors : 0;
}

/**
 * Check if a role allows co-fiduciary (paired) appointments.
 * @param {string} roleId - Role identifier
 * @returns {boolean}
 */
export function allowsPair(roleId) {
  const role = FIDUCIARY_ROLES[roleId];
  return role ? role.allowsPair : false;
}

/**
 * Get the package rules for a given package type.
 * @param {string} packageType - Package name
 * @returns {object|null} Package rules or null if unknown
 */
export function getPackageRules(packageType) {
  return PACKAGE_RULES[packageType] || null;
}

/**
 * Check if a role is conditionally included (e.g., guardian only if minor children).
 * @param {string} roleId - Role identifier
 * @returns {string|null} Condition name or null if unconditional
 */
export function getRoleCondition(roleId) {
  const role = FIDUCIARY_ROLES[roleId];
  return role ? (role.conditionalOn || null) : null;
}

/**
 * Check if a role requires contact information (address, phone).
 * @param {string} roleId - Role identifier
 * @returns {boolean}
 */
export function requiresContact(roleId) {
  const role = FIDUCIARY_ROLES[roleId];
  return role ? (role.requiresContact || false) : false;
}

/**
 * Get all known package type names.
 * @returns {string[]}
 */
export function getAllPackageTypes() {
  return Object.keys(PACKAGE_RULES);
}

/**
 * Check if a package is for married clients.
 * @param {string} packageType - Package name
 * @returns {boolean}
 */
export function isMarriedPackage(packageType) {
  const rules = PACKAGE_RULES[packageType];
  return rules ? rules.marriedPackage : false;
}

export { FIDUCIARY_ROLES, PACKAGE_RULES };
